/**
 * Newsletter form submit UX + stricter email validation.
 * HTML5 accepts "test@gmail"; we require a domain with a TLD (e.g. test@gmail.com).
 */

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

function getInvalidEmailMessage(input) {
  return (
    input?.dataset?.emailError ||
    window.Coco?.formStrings?.invalidEmail ||
    'Please enter a valid email address (e.g. name@example.com).'
  );
}

function isCompleteEmail(value) {
  return EMAIL_REGEX.test(String(value || '').trim());
}

function validateEmailInput(input) {
  if (!input || input.type !== 'email') return true;

  const value = input.value.trim();

  // Empty: let the native `required` message handle it.
  if (!value) {
    input.setCustomValidity('');
    return true;
  }

  if (!isCompleteEmail(value)) {
    input.setCustomValidity(getInvalidEmailMessage(input));
    return false;
  }

  input.setCustomValidity('');
  return true;
}

function bindEmailValidation(root = document) {
  root.querySelectorAll?.('input[type="email"]').forEach((input) => {
    if (input.dataset.emailValidated === 'true') return;
    input.dataset.emailValidated = 'true';

    if (!input.getAttribute('pattern')) {
      input.setAttribute('pattern', '^[^\\s@]+@[^\\s@]+\\.[^\\s@]{2,}$');
    }
    if (!input.getAttribute('title') && !input.dataset.emailError) {
      input.setAttribute('title', getInvalidEmailMessage(input));
    }

    input.addEventListener('input', () => validateEmailInput(input));
    input.addEventListener('blur', () => validateEmailInput(input));
  });
}

class NewsletterForm extends HTMLDivElement {
  constructor() {
    super();
    this.onSubmit = this.onSubmit.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    bindEmailValidation(this);
    this.form.removeEventListener('submit', this.onSubmit);
    this.form.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
  }

  onSubmit(event) {
    const form = event.currentTarget;
    const submitButton = form.querySelector('[data-newsletter-form-button], [data-submit-button], button[type="submit"]');
    const emailInput = form.querySelector('input[type="email"], input[name="contact[email]"]');

    if (emailInput && !validateEmailInput(emailInput)) {
      event.preventDefault();
      emailInput.reportValidity();
      emailInput.focus();
      return;
    }

    if (!emailInput?.value?.trim()) {
      event.preventDefault();
      emailInput?.focus();
      return;
    }

    if (submitButton?.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }

    this.setSubmitLoading(submitButton, true);
    emailInput?.setAttribute('readonly', '');
  }

  setSubmitLoading(button, isLoading) {
    if (!button) return;

    const spinner = button.querySelector('[data-loading-spinner]');

    if (isLoading) {
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
      button.classList.add('loading');
      spinner?.classList.remove('hidden');
      button.querySelectorAll(':scope > *:not([data-loading-spinner])').forEach((element) => {
        element.classList.add('hidden');
      });
      return;
    }

    button.removeAttribute('aria-disabled');
    button.removeAttribute('aria-busy');
    button.classList.remove('loading');
    spinner?.classList.add('hidden');
    button.querySelectorAll(':scope > *:not([data-loading-spinner])').forEach((element) => {
      element.classList.remove('hidden');
    });
  }
}

if (!customElements.get('newsletter-form')) {
  customElements.define('newsletter-form', NewsletterForm, { extends: 'div' });
}

// Contact / comment / gift-card email fields (and dynamically injected forms).
bindEmailValidation(document);

if (!window.__cocoEmailValidationBound) {
  window.__cocoEmailValidationBound = true;

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const emailInputs = form.querySelectorAll('input[type="email"]');
      if (!emailInputs.length) return;

      let firstInvalid = null;
      emailInputs.forEach((input) => {
        if (!validateEmailInput(input) && !firstInvalid) firstInvalid = input;
      });

      if (firstInvalid) {
        event.preventDefault();
        firstInvalid.reportValidity();
        firstInvalid.focus();
      }
    },
    true,
  );

  document.addEventListener('shopify:section:load', (event) => {
    bindEmailValidation(event.target || document);
  });
}